import { dealGames, gamesOf, ownGameIndex, taskKey, tasksForChapter } from '@continuum/show-core';
import type { Chapter, EraLook, GameSet, Profession, TaskDef, TaskState } from '@continuum/show-core';
import { PostLine, mono, sans } from './atoms.js';
import type { WireSeat } from '../room/client.js';
import type { ScreenCopy } from './screenText.js';

export interface QueueRow {
  index: number;
  def: TaskDef;
  mine: boolean;
  /** Mine as well as my own: dealt to me for a job nobody at the table has. */
  extra: boolean;
  done: boolean;
  progress: number;
  /** Whose game it is: a player's call sign, or null when nobody at the table plays it. */
  owner: string | null;
  /** That player's post, under their call sign. */
  ownerPost: Profession | null;
  /** The seat to watch when it is not yours. */
  ownerSeat: number | null;
  /**
   * A saboteur's game of the stop (games/Hack.tsx), in their list only, under an ordinary name: its title.
   * It opens like any game of theirs; to every other screen it is the first game dealt to them.
   */
  hack?: string;
}

/**
 * A saboteur's list (Marek, 2026-10-06): their sabotage in place of the games dealt to them, where the
 * first of those stood, and everyone else's games as before.
 */
export function withSabotage(rows: readonly QueueRow[], hack: QueueRow | null): QueueRow[] {
  if (!hack) return [...rows];
  const first = rows.findIndex((row) => row.mine);
  const before = rows.slice(0, Math.max(0, first)).filter((row) => !row.mine).length;
  const others = rows.filter((row) => !row.mine);
  return [...others.slice(0, before), hack, ...others.slice(before)];
}

/**
 * Every guest sees every game dealt this era (the classic six, or the thirty's up to twelve): theirs are playable, the others can be watched,
 * each with a live progress bar and a player's call sign. Who plays what is the room's own deal
 * (show-core `dealGames`), so a game whose post nobody holds shows its stand-in, never NOBODY.
 */
export function buildQueue(
  chapter: Chapter,
  seats: readonly WireSeat[],
  tasks: Record<string, TaskState>,
  mySeat: number,
  gameSet: GameSet = 'classic',
): QueueRow[] {
  const room = { seats, tasks, gameSet };
  const { players } = dealGames(room, chapter);
  const mineAll = gamesOf(room, chapter, mySeat);
  const games = tasksForChapter(chapter, gameSet);
  const own = ownGameIndex(room, chapter, mySeat);
  const rows = games.map((def, index) => {
    const playing = players[index] ?? [];
    const mine = mineAll.includes(index);
    const ownerSeat = playing.find((s) => s !== mySeat) ?? playing[0] ?? null;
    const owner = ownerSeat === null ? undefined : seats.find((s) => s.seat === ownerSeat);

    let progress = 0;
    let done = false;
    for (const seat of playing) {
      const state = tasks[taskKey(chapter, index, seat)];
      if (!state) continue;
      progress = Math.max(progress, state.progress);
      done ||= state.done;
    }
    if (mine) {
      const own = tasks[taskKey(chapter, index, mySeat)];
      progress = own?.progress ?? 0;
      done = own?.done ?? false;
    }

    return {
      index,
      def,
      mine,
      extra: mine && index !== own,
      done,
      progress,
      owner: owner?.callsign ?? null,
      ownerPost: owner?.profession ?? null,
      ownerSeat: owner ? ownerSeat : null,
      dealt: playing.length > 0 || mine,
    };
  });
  // The thirty deal a second holder's game only where a post has a second holder: the rest are not played.
  return rows.filter((row) => gameSet === 'classic' || row.dealt).map(({ dealt: _, ...row }) => row);
}

export function TaskQueue({
  rows,
  look,
  s,
  onOpen,
  dashed = false,
  labelColor,
  title,
}: {
  rows: QueueRow[];
  look: EraLook;
  s: ScreenCopy;
  onOpen: (row: QueueRow) => void;
  dashed?: boolean;
  labelColor?: string;
  /** The game's name in the guest's language. */
  title: (def: TaskDef) => string;
}) {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: 1.8, color: labelColor ?? look.acc }}>{s.gamesList}</div>
      {rows.map((row) => {
        const statusColor = row.done ? '#7d8e9c' : row.mine ? look.acc : '#8a9aa6';
        const status = row.done ? s.rowDone : row.mine ? s.rowPlay : `${Math.round(row.progress)}%`;
        const who = row.extra ? s.rowExtra : row.mine ? s.rowMine : row.owner ? s.rowWatch(row.owner) : s.rowNobody;
        // Your own game still to play is the one thing on this screen to press: it is the big row.
        const play = row.mine && !row.done;
        return (
          <button
            key={row.index}
            onClick={() => onOpen(row)}
            disabled={!row.mine && !row.owner}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginTop: 8,
              minHeight: play ? 76 : 56,
              padding: play ? '12px 14px' : '8px 12px',
              borderRadius: play ? 12 : 10,
              border: play
                ? `2px solid ${look.acc}`
                : `${dashed ? '1.5px dashed' : '1px solid'} ${row.done ? 'rgba(128,128,128,.25)' : row.mine ? `${look.acc}99` : 'rgba(128,128,128,.3)'}`,
              background: row.mine && !row.done ? `${look.acc}14` : 'rgba(128,128,128,.06)',
              opacity: row.done ? 0.6 : 1,
              color: 'inherit',
              cursor: 'pointer',
              textAlign: 'left',
              boxSizing: 'border-box',
            }}
          >
            <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontFamily: sans, fontSize: play ? 20 : 16, fontWeight: 700, letterSpacing: 0.6, lineHeight: 1.2 }}>{row.hack ?? title(row.def)}</span>
              <span style={{ display: 'block', height: 3, borderRadius: 2, background: 'rgba(128,128,128,.25)', overflow: 'hidden' }}>
                <span
                  style={{
                    display: 'block',
                    height: '100%',
                    width: `${row.done ? 100 : row.progress}%`,
                    background: statusColor,
                    transition: 'width .9s linear',
                  }}
                />
              </span>
              <span style={{ fontFamily: mono, fontSize: 11, letterSpacing: 0.6, color: row.mine ? look.acc : '#8a9aa6' }}>{who}</span>
              {!row.mine && row.owner && row.ownerPost && <PostLine style={{ marginTop: -3, fontSize: 10 }}>{s.post[row.ownerPost]}</PostLine>}
            </span>
            <span
              style={
                play
                  ? {
                      fontFamily: sans,
                      fontSize: 18,
                      fontWeight: 700,
                      letterSpacing: 2,
                      color: '#05070a',
                      background: look.acc,
                      borderRadius: 10,
                      padding: '10px 16px',
                      flex: 'none',
                    }
                  : {
                      fontFamily: mono,
                      fontSize: 11,
                      letterSpacing: 1,
                      color: statusColor,
                      border: `1px solid ${statusColor}`,
                      borderRadius: 6,
                      padding: '3px 7px',
                      flex: 'none',
                    }
              }
            >
              {play ? `${status} ›` : status}
            </span>
          </button>
        );
      })}
    </div>
  );
}
